import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { AdminContainer } from "../../components/layouts";
import MaterialTable from "material-table";
import Time from "../../lithic/Moment";
import api from "../../api";
import {
  apiCollectionToRowResponse,
  rowQueryToApiParams,
} from "../../modules/tableQuery";
import tableIcons from "../../components/tableIcons";
import {
  Select,
  MenuItem,
  Snackbar,
  Box,
  Tooltip,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import AssistantOutlined from "@material-ui/icons/AssistantOutlined";
import useMcpToken from "../../hooks/useMcpToken";
import ActionDialog from "./ActionDialog";
import { executeAction, dismissAction, acceptProposal } from "./applicationAgentApi";

const statusValues = [
  { value: "pending_ops_review", label: "Pending Review" },
  { value: "action_taken", label: "Action Taken" },
  { value: "no_action", label: "No Action" },
  { value: "no_issue", label: "No Issue Detected" },
  { value: "legacy_no_op", label: "Legacy (auto-closed)" },
];

const statusLookup = statusValues.reduce((acc, s) => {
  acc[s.value] = s.label;
  return acc;
}, {});

const detectionResultLookup = {
  issue_detected: "Issue Detected",
  no_issue_detected: "No Issue",
};

const useStyles = makeStyles((theme) => ({
  filterMenuPaper: {
    maxHeight: "none !important",
    height: "auto !important",
  },
  statusCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  proposalIconSlot: {
    width: 20,
    height: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  proposalIcon: {
    color: theme.palette.primary.main,
  },
}));

function truncate(str, len = 80) {
  if (!str) return "";
  return str.length > len ? str.slice(0, len) + "…" : str;
}

const makeFilterSelect = (options, classes) => (props) => {
  const filterValue = props.columnDef.tableData.filterValue || "";
  return (
    <Select
      value={filterValue}
      onChange={(e) =>
        props.onFilterChanged(props.columnDef.tableData.id, e.target.value)
      }
      style={{ width: "100%" }}
      displayEmpty
      MenuProps={{
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        getContentAnchorEl: null,
        PaperProps: { className: classes.filterMenuPaper },
      }}
    >
      <MenuItem value="">
        <em>All</em>
      </MenuItem>
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );
};

const ApplicantAlertsPage = () => {
  const tableRef = useRef(null);
  const location = useLocation();
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alertLoading, setAlertLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  const classes = useStyles();
  const { token: mcpToken, tokenError: mcpTokenError } = useMcpToken();

  const openAlert = (id) => {
    setAlertLoading(true);
    api
      .getApplicantAlert(id)
      .then((resp) => {
        if (resp.data) setSelectedAlert(resp.data);
      })
      .catch(() => {
        setAlert({
          open: true,
          message: `Could not load alert #${id}`,
          severity: "error",
        });
      })
      .finally(() => setAlertLoading(false));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const alertId = params.get("alertId");
    if (alertId) openAlert(alertId);
  }, []);

  const buildActionPayload = (formData) => ({
    action_type: formData.actionType,
    action_category: formData.actionCategory || undefined,
    action_description: formData.actionDescription,
  });

  const handleAcceptProposal = async ({ alertId, proposalId }) => {
    try {
      const tokenResp = await api.requestActionToken(alertId, {
        purpose: "accept_proposal",
        proposal_id: proposalId,
      });
      await acceptProposal(mcpToken, {
        actionToken: tokenResp.data.token,
      });
      setSelectedAlert(null);
      setAlert({
        open: true,
        message: "Proposal accepted",
        severity: "success",
      });
      if (tableRef.current) tableRef.current.onQueryChange();
    } catch (error) {
      if (
        error?.status === 422 &&
        error?.body?.error === "proposal_stale"
      ) {
        const stale = new Error("proposal stale");
        stale.staleProposal = true;
        stale.reason = error.body.reason;
        stale.applicantIds = error.body.applicant_ids;
        throw stale;
      }
      setAlert({
        open: true,
        message: `Error accepting proposal: ${error?.response?.data?.error || error.message || "Unknown error"}`,
        severity: "error",
      });
      throw error;
    }
  };

  const handleActionSubmit = async (formData) => {
    const isNoIntervention = formData.actionType === "no_intervention";
    const isAnsweredQuestion =
      formData.actionType === "intervention" &&
      formData.actionCategory === "answered_question";
    const isResentInvite =
      formData.actionType === "intervention" &&
      formData.actionCategory === "resent_invite";

    try {
      if (isNoIntervention) {
        // No intervention → ai-mainframe dismiss → boompay-api internal dismiss endpoint
        const tokenPayload = {
          action_description: formData.actionDescription,
        };
        const tokenResp = await api.requestActionToken(selectedAlert.id, tokenPayload);
        await dismissAction(mcpToken, {
          actionToken: tokenResp.data.token,
          actionDescription: formData.actionDescription,
        });
      } else if (isAnsweredQuestion || isResentInvite) {
        // Category-specific intervention → ai-mainframe execute-action
        const tokenPayload = {
          action_category: formData.actionCategory,
          message: formData.message,
        };
        if (isResentInvite) {
          tokenPayload.recipients = formData.recipients.map((r) => ({
            pending_magic_link_id: r.pendingMagicLinkId,
          }));
        }
        const tokenResp = await api.requestActionToken(selectedAlert.id, tokenPayload);
        await executeAction(mcpToken, {
          actionToken: tokenResp.data.token,
          actionCategory: formData.actionCategory,
          message: formData.message,
        });
      } else {
        // Direct to boompay-api for "other" and unautomated categories
        await api.submitApplicantAlertAction(selectedAlert.id, buildActionPayload(formData));
      }
      setSelectedAlert(null);
      setAlert({
        open: true,
        message: "Action recorded successfully",
        severity: "success",
      });
      if (tableRef.current) tableRef.current.onQueryChange();
    } catch (error) {
      setAlert({
        open: true,
        message: `Error recording action: ${error?.response?.data?.error || error.message || "Unknown error"}`,
        severity: "error",
      });
    }
  };

  const fetchData = useMemo(() => (q) => {
    const params = rowQueryToApiParams(q);
    const apiParams = {
      page: params.page,
      perPage: params.perPage,
      orderBy: params.orderBy,
      orderDirection: params.orderDirection,
    };
    if (params.filters)
      params.filters.forEach((f) => {
        if (f.values) apiParams[f.name] = f.values;
      });
    return api
      .getApplicantAlerts(apiParams)
      .then((resp) => {
        if (resp.data && resp.data.items)
          return {
            data: resp.data.items,
            page: resp.data.currentPage - 1 || 0,
            totalCount: resp.data.totalCount || 0,
          };
        return apiCollectionToRowResponse(resp);
      })
      .catch(() => {
        setAlert({
          open: true,
          message: "Failed to load alerts",
          severity: "error",
        });
        return { data: [], page: 0, totalCount: 0 };
      });
  }, []);

  const tableOptions = useMemo(() => ({
    columnsButton: false,
    draggable: false,
    debounceInterval: 500,
    emptyRowsWhenPaging: false,
    filtering: true,
    pageSize: 50,
    pageSizeOptions: [50, 100, 200],
    sorting: true,
    thirdSortClick: false,
    filterRowStyle: { position: "sticky", top: 0 },
    headerStyle: { position: "sticky", top: 0 },
  }), []);

  const columns = useMemo(() => [
    {
      title: "Partner",
      field: "partnerName",
      filtering: false,
      sorting: false,
    },
    {
      title: "Applicant",
      field: "applicantName",
      filtering: false,
      sorting: false,
    },
    {
      title: "Message",
      field: "originalMessage",
      filtering: false,
      sorting: false,
      render: (row) => truncate(row.originalMessage),
    },
    {
      title: "Detection",
      field: "detectionResult",
      filtering: true,
      sorting: false,
      lookup: detectionResultLookup,
      filterComponent: makeFilterSelect([
        { value: "issue_detected", label: "Issue Detected" },
        { value: "no_issue_detected", label: "No Issue" },
      ], classes),
      render: (row) => detectionResultLookup[row.detectionResult] || row.detectionResult,
    },
    {
      title: "Timestamp",
      field: "createdAt",
      defaultSort: "desc",
      filtering: false,
      render: (row) => (
        <Time formatExact="MMM DD, YYYY HH:mm">{row.createdAt}</Time>
      ),
    },
    {
      title: "Status",
      field: "status",
      filtering: true,
      sorting: false,
      lookup: statusLookup,
      defaultFilter: "pending_ops_review",
      filterComponent: makeFilterSelect(statusValues, classes),
      render: (row) => (
        <Box className={classes.statusCell}>
          <Box className={classes.proposalIconSlot}>
            {row.hasProposedAction && (
              <Tooltip title="Application Support Agent proposed an action">
                <AssistantOutlined
                  fontSize="small"
                  className={classes.proposalIcon}
                />
              </Tooltip>
            )}
          </Box>
          <span>{statusLookup[row.status] || row.status}</span>
        </Box>
      ),
    },
  ], [classes]);

  return (
    <AdminContainer variant="list">
      <MaterialTable
        title="Applicant Alerts"
        tableRef={tableRef}
        columns={columns}
        options={tableOptions}
        data={fetchData}
        onRowClick={(_event, rowData) => {
          if (rowData) openAlert(rowData.id);
        }}
        icons={tableIcons}
      />

      <ActionDialog
        open={Boolean(selectedAlert) || alertLoading}
        alert={selectedAlert}
        onClose={() => {
          setSelectedAlert(null);
          if (location.search.includes("alertId"))
            window.history.replaceState(null, "", location.pathname);
        }}
        onSubmit={handleActionSubmit}
        onAcceptProposal={handleAcceptProposal}
        mcpToken={mcpToken}
        mcpTokenError={mcpTokenError}
      />

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
          severity={alert.severity}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </AdminContainer>
  );
};

export default ApplicantAlertsPage;
